import { apiErrorMessage } from '@agos/api-client'
import {
  T,
  TICKET_BODY_MAX,
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  TICKET_SUBJECT_MAX,
  formatDateTime,
  franchiseMayMoveTo,
  isTicketOutstanding,
  priorityNeedsSaying,
  ticketDraftProblem,
  ticketStatusLabel,
  ticketStatusTone,
  tt,
  type TicketStatus,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  EmptyState,
  PageHeader,
  Spinner,
  StatusBadge,
  cn,
  useToast,
} from '@agos/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LifeBuoy, Plus, Send } from 'lucide-react'
import { useState } from 'react'

import {
  fetchThread,
  fetchTickets,
  inOrder,
  isFromPlatform,
  outstandingCount,
  raiseTicket,
  replyToTicket,
  type TicketSummary,
} from './support'

/**
 * A franchise reports a problem to the platform - `FRONTEND.md` §9.513, owner O31.
 *
 * **The owner:** *"there make complex ticket system, where they can report bugs etc to main admins from SaaS"*.
 *
 * **Every rule on this screen is `@agos/shared`'s and every refusal is the server's.** The statuses, their labels,
 * the categories, the priorities, the two length caps and which statuses this side may set all come from §9.510, and
 * a backend guard holds them to `SupportTicketRules`. Nothing here decides anything - it draws what the rules allow,
 * so a screen that offered a button the server refuses would be a code change in one place rather than a drift
 * between two.
 */

const TICKETS_KEY = ['support-tickets'] as const

export function SupportPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [openId, setOpenId] = useState<number | null>(null)
  const [composing, setComposing] = useState(false)

  const tickets = useQuery({ queryKey: TICKETS_KEY, queryFn: fetchTickets })

  return (
    <main className="p-4 space-y-4">
      <PageHeader
        title={tt('Podpora')}
        description={tt('Nahláste problém alebo otázku prevádzkovateľovi systému.')}
      />

      {tickets.isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {tickets.isError && (
        <p className="text-sm text-red-500" data-support-error>
          {apiErrorMessage(tickets.error, tt('Tikety sa nepodarilo načítať.'))}
        </p>
      )}

      {!tickets.isLoading && !tickets.isError && (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground" data-support-outstanding>
              {outstandingCount(tickets.data ?? [])} <T>otvorených</T>
            </p>
            <button
              type="button"
              data-new-ticket
              onClick={() => setComposing((was) => !was)}
              className={cn(
                'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm',
                BUTTON_PRIMARY,
              )}
            >
              <Plus className="w-4 h-4" />
              <T>Nový tiket</T>
            </button>
          </div>

          {composing && (
            <NewTicket
              onDone={() => {
                setComposing(false)
                void queryClient.invalidateQueries({ queryKey: TICKETS_KEY })
              }}
              onFailed={(message) =>
                toast({ variant: 'destructive', title: tt('Chyba'), description: message })
              }
            />
          )}

          {(tickets.data ?? []).length === 0 && !composing && (
            <EmptyState
              icon={<LifeBuoy className="w-6 h-6" />}
              title={tt('Žiadne tikety')}
              description={tt('Keď niečo nefunguje, nahláste to tu.')}
            />
          )}

          <ul className="space-y-1.5" data-ticket-list>
            {(tickets.data ?? []).map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                open={openId === ticket.id}
                onToggle={() => setOpenId(openId === ticket.id ? null : ticket.id)}
              />
            ))}
          </ul>
        </>
      )}
    </main>
  )
}

/** The form, which refuses before it posts so a long description is never typed and then lost. */
function NewTicket({
  onDone,
  onFailed,
}: {
  onDone: () => void
  onFailed: (message: string) => void
}) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState<string>('bug')
  const [priority, setPriority] = useState<string>('normal')

  const raise = useMutation({
    mutationFn: () => raiseTicket({ subject, body, category, priority }),
    onSuccess: onDone,
    onError: (failure: unknown) =>
      onFailed(apiErrorMessage(failure, tt('Tiket sa nepodarilo vytvoriť.'))),
  })

  /* §9.510's own rules, so the sentence the operator reads here and the one the server would answer are the same.
   * Checked on every render rather than on submit: a disabled button with the reason beside it is better than a
   * refusal after the writing. */
  const problem = ticketDraftProblem({ subject, body, category, priority })

  return (
    <section className="rounded-xl border border-border p-3 space-y-2" data-new-ticket-form>
      <input
        value={subject}
        maxLength={TICKET_SUBJECT_MAX}
        onChange={(event) => setSubject(event.target.value)}
        placeholder={tt('Čo sa stalo, v jednej vete')}
        aria-label={tt('Predmet')}
        data-ticket-subject
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
      />
      <div className="flex flex-wrap gap-2">
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label={tt('Kategória')}
          data-ticket-category
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
        >
          {TICKET_CATEGORIES.map((one) => (
            <option key={one} value={one}>
              {TICKET_CATEGORY_LABELS[one]}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
          aria-label={tt('Priorita')}
          data-ticket-priority
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
        >
          {TICKET_PRIORITIES.map((one) => (
            <option key={one} value={one}>
              {TICKET_PRIORITY_LABELS[one]}
            </option>
          ))}
        </select>
      </div>
      <textarea
        value={body}
        maxLength={TICKET_BODY_MAX}
        onChange={(event) => setBody(event.target.value)}
        rows={5}
        placeholder={tt('Popíšte, čo sa stalo a kedy. Čím konkrétnejšie, tým rýchlejšie to vyriešime.')}
        aria-label={tt('Popis')}
        data-ticket-body
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
      />
      <div className="flex items-center justify-between gap-2">
        {problem !== null ? (
          <p className="text-xs text-amber-500" data-ticket-problem>
            {problem}
          </p>
        ) : (
          <span />
        )}
        <button
          type="button"
          data-ticket-send
          disabled={problem !== null || raise.isPending}
          onClick={() => raise.mutate()}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm disabled:opacity-40',
            BUTTON_PRIMARY,
          )}
        >
          {raise.isPending ? <Spinner className="w-4 h-4" /> : <Send className="w-4 h-4" />}
          <T>Odoslať</T>
        </button>
      </div>
    </section>
  )
}

function TicketRow({
  ticket,
  open,
  onToggle,
}: {
  ticket: TicketSummary
  open: boolean
  onToggle: () => void
}) {
  return (
    <li
      /* §9.517 - the owner asked twice for a better design on tickets, and the two screens now read the
         same way: a left accent on what is waiting, the status in colour, the priority only when it says
         something. This side had a two-tone status - amber or grey - so *Riešime* and *Uzavretá* were the
         same grey, which is the distinction a franchise most wants. */
      className={cn(
        'rounded-xl border border-border border-l-2 overflow-hidden',
        isTicketOutstanding(ticket.status) ? 'border-l-amber-500' : 'border-l-transparent',
      )}
      data-ticket={String(ticket.id)}
    >
      <button
        type="button"
        onClick={onToggle}
        data-ticket-open
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium truncate">{ticket.subject}</span>
          <span className="block text-[11px] text-muted-foreground">
            {TICKET_CATEGORY_LABELS[ticket.category]} &middot; {formatDateTime(ticket.created_at)}
          </span>
        </span>
        <span className="shrink-0 flex items-center gap-1.5">
          {priorityNeedsSaying(ticket.priority) && (
            <span data-ticket-priority={ticket.priority}>
              <StatusBadge
                label={TICKET_PRIORITY_LABELS[ticket.priority]}
                color={ticket.priority === 'low' ? 'zinc' : 'red'}
              />
            </span>
          )}
          <span data-ticket-status={ticket.status}>
            <StatusBadge
              label={ticketStatusLabel(ticket.status, 'franchise')}
              color={ticketStatusTone(ticket.status)}
            />
          </span>
        </span>
      </button>
      {open && <Thread ticketId={ticket.id} status={ticket.status} />}
    </li>
  )
}

/** The conversation, and the one form that both replies and moves the ticket. */
function Thread({ ticketId, status }: { ticketId: number; status: TicketStatus }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [reply, setReply] = useState('')

  const thread = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => fetchThread(ticketId),
  })

  const send = useMutation({
    mutationFn: (next?: TicketStatus) => replyToTicket(ticketId, reply, next),
    onSuccess: () => {
      setReply('')
      void queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] })
      void queryClient.invalidateQueries({ queryKey: TICKETS_KEY })
    },
    onError: (failure: unknown) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(failure, tt('Správu sa nepodarilo odoslať.')),
      }),
  })

  return (
    <div className="border-t border-border p-3 space-y-2" data-ticket-thread>
      {thread.isLoading && <Spinner className="w-4 h-4" />}
      {thread.isError && (
        <p className="text-xs text-red-500">
          {apiErrorMessage(thread.error, tt('Konverzáciu sa nepodarilo načítať.'))}
        </p>
      )}
      {inOrder(thread.data?.messages ?? []).map((message) => (
        <div
          key={message.id}
          data-ticket-message={isFromPlatform(message) ? 'platform' : 'franchise'}
          className={cn(
            'rounded-lg p-2 text-xs',
            isFromPlatform(message) ? 'bg-primary/10 ml-6' : 'bg-muted/50 mr-6',
          )}
        >
          <p className="font-semibold text-[11px] text-muted-foreground">
            {message.author_name ?? (isFromPlatform(message) ? tt('Platforma') : tt('Franšíza'))}{' '}
            &middot; {formatDateTime(message.created_at)}
          </p>
          <p className="whitespace-pre-wrap">{message.body}</p>
        </div>
      ))}

      <textarea
        value={reply}
        maxLength={TICKET_BODY_MAX}
        onChange={(event) => setReply(event.target.value)}
        rows={3}
        placeholder={tt('Odpoveď')}
        aria-label={tt('Odpoveď')}
        data-ticket-reply
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-ticket-reply-send
          disabled={reply.trim() === '' || send.isPending}
          onClick={() => send.mutate(undefined)}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs disabled:opacity-40',
            BUTTON_PRIMARY,
          )}
        >
          <Send className="w-3.5 h-3.5" />
          <T>Odoslať</T>
        </button>
        {/* §9.510's `franchiseMayMoveTo`, so this side cannot offer a status the server refuses - and the reply
          * rides along, because a franchise closing a ticket almost always wants to say why. */}
        {franchiseMayMoveTo(status).map((next) => (
          <button
            key={next}
            type="button"
            data-ticket-move={next}
            disabled={send.isPending}
            onClick={() => send.mutate(next)}
            className="px-4 py-2 rounded-lg border border-border text-xs font-semibold disabled:opacity-40"
          >
            {ticketStatusLabel(next, 'franchise')}
          </button>
        ))}
      </div>
    </div>
  )
}
